import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import UsersPage from './pages/UsersPage';
import ConnectionsPage from './pages/ConnectionsPage';
import GeneratedDocumentsPage from './pages/GeneratedDocumentsPage';
import DataSourceDetailPage from './pages/DataSourceDetailPage';
import FolderDetailPage from './pages/FolderDetailPage';
import AddConnectionPage from './pages/AddConnectionPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import ActivityPage from './pages/ActivityPage';
import DocumentLibraryPage from './pages/DocumentLibraryPage';
import KnowledgePage from './pages/KnowledgePage';
import TimelinePage from './pages/TimelinePage';
import ModelPage from './pages/ModelPage';
import PlansPage from './pages/PlansPage';
import ConnectPage from './pages/ConnectPage';
import TasksPage from './pages/TasksPage';
import ReviewPage from './pages/ReviewPage';
import SkillsPage from './pages/SkillsPage';
import WatchesPage from './pages/WatchesPage';
import MarketPage from './pages/MarketPage';
import StatusPage from './pages/StatusPage';
import UsagePage from './pages/UsagePage';
import WhatsNewPage from './pages/WhatsNewPage';
import FileDetailPage from './pages/FileDetailPage';
import DocumentTypeDetailPage from './pages/DocumentTypeDetailPage';
import AddDocumentTypePage from './pages/AddDocumentTypePage';
import LoginPage from './pages/LoginPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import SectionPage from './pages/SectionPage';
import WorkspaceIndexPage from './pages/WorkspaceIndexPage';
import HomePage from './pages/HomePage';
import GraphPage from './pages/GraphPage';
import Layout from './components/Layout';
import ToastContainer from './components/ToastContainer';
import { useAuth } from './contexts/AuthContext';

const ProtectedLayout = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const fullPath = location.pathname + location.search;
    const redirectParam = location.pathname !== '/'
      ? `?redirect=${encodeURIComponent(fullPath)}`
      : '';
    return <Navigate to={`/login${redirectParam}`} replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

const App = () => {
  const { isAuthenticated, loading, refreshUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      refreshUser();
    }
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.svg" alt="Navaris" className="w-12 h-12 mx-auto mb-4 animate-pulse" />
          <Loader2 className="w-8 h-8 animate-spin text-brand mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/connect" element={<ConnectPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/connections" element={<ConnectionsPage />} />
          <Route path="/generated" element={<GeneratedDocumentsPage />} />
          <Route path="/connections/add" element={<AddConnectionPage />} />
          <Route path="/connections/:sourceId" element={<DataSourceDetailPage />} />
          <Route path="/connections/:sourceId/folder" element={<FolderDetailPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/whats-new" element={<WhatsNewPage />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/library" element={<DocumentLibraryPage />} />
          <Route path="/knowledge" element={<KnowledgePage />} />
          <Route path="/graph" element={<GraphPage />} />
          <Route path="/model" element={<ModelPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/review" element={<ReviewPage />} />
          <Route path="/skills" element={<SkillsPage />} />
          <Route path="/watches" element={<WatchesPage />} />
          <Route path="/market" element={<MarketPage />} />
          <Route path="/timeline" element={<TimelinePage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:jobId" element={<JobDetailPage />} />
          <Route path="/files/:fileId" element={<FileDetailPage />} />
          <Route path="/types/add" element={<AddDocumentTypePage />} />
          <Route path="/types/:typeId" element={<DocumentTypeDetailPage />} />
          <Route path="/workspace" element={<WorkspaceIndexPage />} />
          <Route path="/workspace/:sectionPath" element={<SectionPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
};

export default App;
